// Dieline model: a hinge tree of panels shared by the 2D dieline and the 3D fold preview. Units: mm.
const D2R = Math.PI / 180;

const node = (depth, opts = {}) => ({ depth, taper: 0, angle: 90, children: {}, ...opts });

// Bounding box of a node in its own plane, given its extension direction and hinge length.
export function bbox(dir, a, b) {
  if (dir === "+y") return [0, 0, a, b];
  if (dir === "-y") return [0, -b, a, 0];
  if (dir === "+x") return [0, 0, b, a];
  return [-b, 0, 0, a];
}

// Panel outline (far corners inset by taper) in the node's own plane.
export function shape(dir, a, b, t) {
  const uv = [[0, 0], [a, 0], [a - t, b], [t, b]];
  return uv.map(([u, v]) => (dir === "+y" ? [u, v] : dir === "-y" ? [u, -v] : dir === "+x" ? [v, u] : [-v, u]));
}

export function childOrigin(edge, box) {
  const [x0, y0, x1, y1] = box;
  if (edge === "top") return { at: [x0, y1], dir: "+y", len: x1 - x0 };
  if (edge === "bottom") return { at: [x0, y0], dir: "-y", len: x1 - x0 };
  if (edge === "right") return { at: [x1, y0], dir: "+x", len: y1 - y0 };
  return { at: [x0, y0], dir: "-x", len: y1 - y0 };
}

const tuckD = (W) => Math.min(18, Math.max(8, W * 0.35));
const dust = (W, D) => node(Math.min(W * 0.5, D * 0.45), { taper: W * 0.18, angle: 88 });
const lid = (W, tuck = true) => node(W, { children: tuck ? { top: node(tuckD(W), { taper: tuckD(W) * 0.35 }) } : {} });
const lidDown = (W) => node(W, { children: { bottom: node(tuckD(W), { taper: tuckD(W) * 0.35 }) } });
const glue = () => node(15, { taper: 4 });

export const STYLES = {
  rte: "Reverse tuck end",
  ste: "Straight tuck end",
  crash: "Crash-lock bottom",
  mailer: "Mailer / e-commerce box",
  sleeve: "Sleeve",
};

// Returns the root node. Root is the front panel (L wide, D tall) extending +y from origin.
export function buildModel(style, L, W, D) {
  if (style === "mailer") {
    const ear = () => node(Math.min(W * 0.45, 40), { taper: Math.min(W * 0.2, 15), angle: 90 });
    return {
      len: L, ...node(W, { angle: 0 }),
      children: {
        bottom: node(D, { children: { left: ear(), right: ear() } }),
        left: node(D),
        right: node(D),
        top: node(D, { children: { top: node(W, { children: { top: node(D, { children: { top: node(tuckD(W), { taper: 4 }) } }), left: node(D * 0.9, { taper: D * 0.2 }), right: node(D * 0.9, { taper: D * 0.2 }) } }) } }),
      },
    };
  }
  const side = (kids) => node(W, { children: kids });
  const top = style === "sleeve" ? {} : { top: dust(W, D) };
  const bottomDust = style === "sleeve" || style === "crash" ? {} : { bottom: dust(W, D) };
  const crashF = (depth) => (style === "crash" ? { bottom: node(depth, { taper: depth * 0.55, angle: 90 }) } : {});
  const back = { ...(style !== "sleeve" ? { top: lid(W) } : {}), ...(style === "ste" ? { bottom: lidDown(W) } : {}), ...crashF(W * 0.75) };
  const front = { left: glue(), ...(style === "rte" ? { bottom: lidDown(W) } : {}), ...crashF(W * 0.75) };
  return {
    len: L, ...node(D, { angle: 0 }),
    children: {
      ...front,
      right: side({ ...top, ...bottomDust, ...crashF(W * 0.5), right: node(L, { children: { ...back, right: side({ ...top, ...bottomDust, ...crashF(W * 0.5) }) } }) }),
    },
  };
}

// Flatten the tree into absolute 2D polygons.
export function flatten(root) {
  const polys = [];
  const walk = (n, dir, len, ox, oy) => {
    polys.push(shape(dir, len, n.depth, n.taper).map(([x, y]) => [x + ox, y + oy]));
    const box = bbox(dir, len, n.depth);
    Object.entries(n.children).forEach(([edge, c]) => {
      const o = childOrigin(edge, box);
      walk(c, o.dir, o.len, ox + o.at[0], oy + o.at[1]);
    });
  };
  walk(root, "+y", root.len, 0, 0);
  return polys;
}

// Edges shared by two panels are creases; the rest are cuts.
export function dielineLines(polys) {
  const key = (p) => `${p[0].toFixed(2)},${p[1].toFixed(2)}`;
  const map = new Map();
  polys.forEach((poly) => poly.forEach((p, i) => {
    const q = poly[(i + 1) % poly.length];
    if (Math.hypot(p[0] - q[0], p[1] - q[1]) < 0.01) return;
    const k = [key(p), key(q)].sort().join("|");
    const e = map.get(k);
    if (e) e.count += 1; else map.set(k, { a: p, b: q, count: 1 });
  }));
  const xs = polys.flat().map((p) => p[0]), ys = polys.flat().map((p) => p[1]);
  const minX = Math.min(...xs), minY = Math.min(...ys), maxX = Math.max(...xs), maxY = Math.max(...ys);
  // Normalise to top-left origin with y down for export.
  const n = (p) => [p[0] - minX, maxY - p[1]];
  const lines = [...map.values()].map((e) => ({ a: n(e.a), b: n(e.b), type: e.count > 1 ? "crease" : "cut" }));
  return { lines, width: maxX - minX, height: maxY - minY };
}

export function toSVG({ lines, width, height }, pad = 10) {
  const W = width + pad * 2, H = height + pad * 2;
  const segs = lines.map((l) => `<line x1="${(l.a[0] + pad).toFixed(2)}" y1="${(l.a[1] + pad).toFixed(2)}" x2="${(l.b[0] + pad).toFixed(2)}" y2="${(l.b[1] + pad).toFixed(2)}" class="${l.type}"/>`).join("\n");
  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${W.toFixed(2)}mm" height="${H.toFixed(2)}mm" viewBox="0 0 ${W.toFixed(2)} ${H.toFixed(2)}">
<style>.cut{stroke:#E11D48;stroke-width:0.35;fill:none}.crease{stroke:#2563EB;stroke-width:0.35;stroke-dasharray:3 2;fill:none}</style>
${segs}
</svg>`;
}

export function toDXF({ lines, height }) {
  const ent = lines.map((l) => ["0", "LINE", "8", l.type === "cut" ? "CUT" : "CREASE", "62", l.type === "cut" ? "1" : "5",
    "10", l.a[0].toFixed(3), "20", (height - l.a[1]).toFixed(3), "30", "0", "11", l.b[0].toFixed(3), "21", (height - l.b[1]).toFixed(3), "31", "0"].join("\n")).join("\n");
  return ["0", "SECTION", "2", "HEADER", "9", "$INSUNITS", "70", "4", "0", "ENDSEC",
    "0", "SECTION", "2", "TABLES", "0", "TABLE", "2", "LAYER", "70", "2",
    "0", "LAYER", "2", "CUT", "70", "0", "62", "1", "6", "CONTINUOUS",
    "0", "LAYER", "2", "CREASE", "70", "0", "62", "5", "6", "DASHED", "0", "ENDTAB", "0", "ENDSEC",
    "0", "SECTION", "2", "ENTITIES", ent, "0", "ENDSEC", "0", "EOF"].join("\n");
}

export async function toPDF(d, title, pad = 10) {
  const { jsPDF } = await import("jspdf");
  const W = d.width + pad * 2, H = d.height + pad * 2 + 8;
  const pdf = new jsPDF({ orientation: W > H ? "l" : "p", unit: "mm", format: [W, H] });
  pdf.setFontSize(7);
  pdf.text(`${title}  ·  red = cut, blue dashed = crease  ·  GoPrintPack`, pad, H - 4);
  pdf.setLineWidth(0.3);
  d.lines.forEach((l) => {
    if (l.type === "cut") { pdf.setDrawColor(225, 29, 72); pdf.setLineDashPattern([], 0); } else { pdf.setDrawColor(37, 99, 235); pdf.setLineDashPattern([3, 2], 0); }
    pdf.line(l.a[0] + pad, l.a[1] + pad, l.b[0] + pad, l.b[1] + pad);
  });
  return pdf.output("blob");
}

export const deg = (a) => a * D2R;

export function download(content, name, type) {
  const blob = content instanceof Blob ? content : new Blob([content], { type });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}
